import { makeAutoObservable } from 'mobx';

import CollectionPreviewStore from '@/collection/stores/CollectionPreviewStore';
import Notifier from '@/common/services/Notifier';
import { CopyDriveItemCommand } from '@/drive/entities/DriveCommand/CopyDriveItemCommand';
import { MoveDriveItemCommand } from '@/drive/entities/DriveCommand/MoveDriveItemCommand';
import { SubscribeToCollectionCommand } from '@/drive/entities/DriveCommand/SubscribeToCollectionCommand';
import {
  AddToCollectionRequest,
  BatchAddDriveItemsCommand,
  DriveDestinationCommand,
} from '@/drive/entities/DriveDestinationCommand';
import { ProductItem } from '@/drive/entities/DriveItem/DriveItem';
import { DriveItemsResponse } from '@/drive/entities/DriveItemsResponse';
import { CollectionTreeItem } from '@/drive/entities/DriveTreeItem/CollectionTreeItem';
import { DriveTreeItem } from '@/drive/entities/DriveTreeItem/DriveTreeItem';
import { notifyAccessInheritanceWarnings } from '@/drive/helpers/notifyAccessInheritanceWarnings';
import { resolveAccessInheritance } from '@/drive/helpers/resolveAccessInheritance';
import { toDriveItemsErrorResponse } from '@/drive/helpers/toDriveItemsErrorResponse';
import { DriveModifierStore } from '@/drive/stores/DriveModifierStore/DriveModifierStore';
import { AccessInheritanceDecision } from '@/drive/types/AccessInheritanceDecision';
import { AccessInheritanceSubject } from '@/drive/types/AccessInheritanceSubject';
import { ForwardNotificationService } from '@/forwarding/services/ForwardNotificationService';
import { ForwardForm } from '@/forwarding/types/ForwardForm';
import { EntityAccessChangedEvent, EntityAccessEventEmitter } from '@/security/services/EntityAccessEventEmitter';

import { DriveDestinationContext } from './DriveDestinationStore.types';
import { getAddedItemAccessChanges, toEntityAccessChanges } from './DriveDestinationStore.utils';

class DriveDestinationStore {
  isSubmitting = false;

  constructor(
    private driveModifierStore: DriveModifierStore,
    private collectionPreviewStore: CollectionPreviewStore,
    private entityAccessEventEmitter: EntityAccessEventEmitter,
    private shouldNotifySuccess = true,
  ) {
    makeAutoObservable<DriveDestinationStore, 'baseExecute' | 'shouldNotifySuccess'>(this, {
      baseExecute: false,
      shouldNotifySuccess: false,
    });
  }

  executeCommand = async (
    command: DriveDestinationCommand,
    form: ForwardForm,
    destination: DriveTreeItem,
    readOnly = false,
  ): Promise<Nullable<DriveItemsResponse>> => {
    const decision = await this.resolveAccessDecision(command, destination);

    if (decision === AccessInheritanceDecision.Cancel) {
      return null;
    }

    this.setSubmitting(true);

    try {
      const res = await this.baseExecute(command, {
        form,
        destination,
        options: { readOnly, shouldInheritAccess: decision === AccessInheritanceDecision.Inherit },
      });

      if (res.hasErrors()) {
        res.getErrors().forEach((error) => Notifier.error(error));
      }

      notifyAccessInheritanceWarnings(res.getAccessChanges());

      return res;
    } finally {
      this.setSubmitting(false);
    }
  };

  private setSubmitting = (isSubmitting: boolean) => {
    this.isSubmitting = isSubmitting;
  };

  private async resolveAccessDecision(
    command: DriveDestinationCommand,
    destination: DriveTreeItem,
  ): Promise<AccessInheritanceDecision> {
    const accessSubjects = await this.collectAccessSubjects(command);

    return resolveAccessInheritance(accessSubjects, destination);
  }

  private async collectAccessSubjects(command: DriveDestinationCommand): Promise<AccessInheritanceSubject[]> {
    this.setSubmitting(true);

    try {
      return await command.getAccessSubjects();
    } finally {
      this.setSubmitting(false);
    }
  }

  private baseExecute = async (
    command: DriveDestinationCommand,
    context: DriveDestinationContext,
  ): Promise<DriveItemsResponse> => {
    if (command instanceof SubscribeToCollectionCommand) {
      return this.executeSubscribeToCollection(command, context.destination);
    }

    if (command instanceof CopyDriveItemCommand) {
      return this.executeCopyDriveItem(command, context);
    }

    if (command instanceof MoveDriveItemCommand) {
      return this.executeMoveDriveItem(command, context);
    }

    return this.executeBatchAdd(command, context);
  };

  private async executeSubscribeToCollection(
    command: SubscribeToCollectionCommand,
    destination: DriveTreeItem,
  ): Promise<DriveItemsResponse> {
    const res = await this.driveModifierStore.subscribeToCollection(command.collectionNodeId, destination.getDriveId());

    if (!res.hasErrors() && this.shouldNotifySuccess) {
      ForwardNotificationService.addedSuccess(destination);
    }

    return res;
  }

  private async executeCopyDriveItem(
    command: CopyDriveItemCommand,
    context: DriveDestinationContext,
  ): Promise<DriveItemsResponse> {
    const { form, destination, options } = context;

    if (destination instanceof CollectionTreeItem) {
      return this.executeBatchAddToCollection(command.collectionRequest, { ...context, destination });
    }

    const res = await this.driveModifierStore.copyDriveItem(command.driveItem, form, destination.getDriveId(), options);

    if (!res.hasErrors() && this.shouldNotifySuccess) {
      ForwardNotificationService.addedSuccess(destination);
    }

    return res;
  }

  private async executeMoveDriveItem(
    command: MoveDriveItemCommand,
    context: DriveDestinationContext,
  ): Promise<DriveItemsResponse> {
    const { form, destination, options } = context;

    if (destination instanceof CollectionTreeItem) {
      return this.executeMoveDriveItemToCollection(command, { ...context, destination });
    }

    const res = await this.driveModifierStore.move(
      command.driveItem,
      form,
      destination.getDriveId(),
      command.currentParentId,
      options,
    );

    if (!res.hasErrors() && this.shouldNotifySuccess) {
      ForwardNotificationService.movedSuccess(destination);
    }

    return res;
  }

  private async executeMoveDriveItemToCollection(
    command: MoveDriveItemCommand,
    { form, destination, options }: DriveDestinationContext<CollectionTreeItem>,
  ): Promise<DriveItemsResponse> {
    if (!command.collectionRequest) {
      throw new Error('collectionRequest required for moveToCollection');
    }

    const movedItem = command.collectionRequest.productItem;

    try {
      const accessChanges = await this.driveModifierStore.moveToCollection(
        movedItem,
        destination.getDriveId(),
        destination.collectionId,
        command.currentParentId,
        form,
        options,
      );

      this.emitAccessChanges(toEntityAccessChanges(accessChanges));

      this.collectionPreviewStore.markAsStale([destination.collectionId]);

      if (this.shouldNotifySuccess) {
        ForwardNotificationService.movedSuccess(destination);
      }

      return new DriveItemsResponse<ProductItem>([movedItem], accessChanges);
    } catch (error) {
      return toDriveItemsErrorResponse<ProductItem>(error, 'Failed to move');
    }
  }

  private executeBatchAdd(
    command: BatchAddDriveItemsCommand,
    context: DriveDestinationContext,
  ): Promise<DriveItemsResponse> {
    const { destination } = context;

    return destination instanceof CollectionTreeItem
      ? this.executeBatchAddToCollection(command.collectionRequest, { ...context, destination })
      : this.executeBatchAddToDrive(command, context);
  }

  private async executeBatchAddToCollection(
    collectionRequest: Nullable<AddToCollectionRequest>,
    { form, destination, options }: DriveDestinationContext<CollectionTreeItem>,
  ): Promise<DriveItemsResponse> {
    if (!collectionRequest) {
      throw new Error('collectionRequest required for addToCollection');
    }

    try {
      const responses = await this.driveModifierStore.addToCollection(
        destination.getDriveId(),
        destination.collectionId,
        collectionRequest.productIds,
        form,
        options,
      );

      this.emitAccessChanges(toEntityAccessChanges(responses));

      this.collectionPreviewStore.markAsStale([destination.collectionId]);

      if (this.shouldNotifySuccess) {
        ForwardNotificationService.addedSuccess(destination);
      }

      return new DriveItemsResponse([], responses);
    } catch (error) {
      return toDriveItemsErrorResponse(error, 'Failed to add products to collection');
    }
  }

  private async executeBatchAddToDrive(
    command: BatchAddDriveItemsCommand,
    { form, destination, options }: DriveDestinationContext,
  ): Promise<DriveItemsResponse> {
    const sourceItems = await command.getSourceItems();
    const res = await this.driveModifierStore.addToFolder(
      command.requests,
      sourceItems,
      form,
      destination.getDriveId(),
      options,
    );

    if (res.hasErrors()) {
      return res;
    }

    this.emitAccessChanges(getAddedItemAccessChanges(res));

    if (this.shouldNotifySuccess) {
      ForwardNotificationService.addedSuccess(destination);
    }

    return res;
  }

  private emitAccessChanges(events: EntityAccessChangedEvent[]) {
    events.forEach((event) => this.entityAccessEventEmitter.emitAccessChange(event.targetType, event.changes));
  }
}

export default DriveDestinationStore;
